import { useLazyQuery } from "@apollo/client";
import { compact, uniqBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

const MeetingOverview = ({
  selectedDay,
  filterClassName,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  filterClassName?: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const selectedUser = currentUserVar();
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [loadMore, setLoadMore] = useState<"past" | "future" | null>(null);
  const [meetings, setMeetings] = useState<
    (MeetingsOverviewMeetingFragment | MeetingDialogFragmentFragment)[]
  >([]);
  const [participantFilter, setParticipantFilter] = useState<number | null>(
    null,
  );
  const [meetingTypeFilter, setMeetingTypeFilter] = useState(MeetingType.ALL);

  const [meetingDisplayedRange, setMeetingDisplayedRange] = useState<{
    start: string;
    end: string;
  }>({
    start: moment(selectedDay).subtract(1, "week").startOf("isoWeek").format(),
    end: moment(selectedDay).endOf("isoWeek").format(),
  });

  const [fetchMeetings, { loading, data }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {});

  const scrollToToday = () => {
    if (todayRef.current && scrollContainerRef.current) {
      const scrollTop = todayRef.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: "smooth",
      });
    }
  };

  const fetchMoreMeetings = (dateRangeStart: string, dateRangeEnd: string) => {
    return fetchMeetings({
      variables: {
        participants: [selectedUser.id],
        dateRangeStart: dateRangeStart,
        dateRangeEnd: dateRangeEnd,
        orderBy: "start_datetime",
        first: null,
      },
      context: { merge: true },
    });
  };

  useEffect(() => {
    if (forceTodayScroll > 0) {
      scrollToToday();
    }
  }, [forceTodayScroll]);

  useEffect(() => {
    if (loadMore === null) {
      return;
    }
    const dateRangeStart =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start).subtract(1, "week").format()
        : meetingDisplayedRange.end;
    const dateRangeEnd = moment(dateRangeStart).add(1, "week").format();
    const sortedDates = [
      meetingDisplayedRange.start,
      meetingDisplayedRange.end,
      dateRangeStart,
      dateRangeEnd,
    ].sort();
    setMeetingDisplayedRange({
      start: sortedDates[0],
      end: sortedDates[3],
    });
    fetchMoreMeetings(dateRangeStart, dateRangeEnd).finally(() => {
      setLoadMore(null);
    });
    // only want to fetch more when index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMore]);

  const {
    options: assessmentUserFilterOptions,
    query,
    setQuery,
  } = useUserComboboxQuery({});
  const userComboboxValue =
    assessmentUserFilterOptions.find(({ id }) => id === participantFilter) ||
    null;

  const handleChangeParticipantFilter = useCallback(
    (option?: UserComboboxOption) => {
      setParticipantFilter(
        option && typeof option.id === "number" ? option.id : null,
      );
    },
    [],
  );

  useEffect(() => {
    fetchMeetings({
      variables: {
        participants: [selectedUser.id],
        dateRangeStart: meetingDisplayedRange.start,
        dateRangeEnd: meetingDisplayedRange.end,
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: () => {
        fetchMoreMeetings(
          moment(selectedDay).add(1, "week").startOf("isoWeek").format(),
          moment(selectedDay).add(1, "week").endOf("isoWeek").format(),
        ).then(() => {
          setTimeout(() => {
            scrollToToday();
          }, 100);
          setMeetingDisplayedRange((prev) => ({
            start: prev.start,
            end: moment(selectedDay).add(1, "week").endOf("isoWeek").format(),
          }));
        });
      },
    });
  }, []);

  const newMeetingItems = useMemo(
    () =>
      uniqBy(
        data?.meetings ? compact(assertEdgesNonNull(data.meetings)) : [],
        "id",
      ),
    [data],
  );

  useEffect(() => {
    setMeetings(uniqBy([...meetings, ...newMeetingItems], "id"));
  }, [newMeetingItems]);

  const meetingsToDisplay = meetings.length > 0 ? meetings : newMeetingItems;

  return (
    <div className="@container/overview-meetings h-full">
      <div className={classNames("sticky z-40 bg-white")}>
        <div
          className={classNames(
            "flex items-center justify-between gap-2 h-14 px-4 border-b",
            filterClassName,
          )}
        >
          <div className="w-1/2">
            <UserCombobox
              onChangeValue={handleChangeParticipantFilter}
              options={assessmentUserFilterOptions}
              value={userComboboxValue}
              placeholder="All people"
              clearable={participantFilter !== null}
              query={query}
              onChangeQuery={setQuery}
              onClearValue={handleChangeParticipantFilter}
              buttonProps={{ size: ButtonSize.medium }}
            />
          </div>
          <div className="w-1/2 flex">
            <div className="flex-1 w-full">
              <Select
                value={meetingTypeFilter}
                options={[
                  {
                    value: MeetingType.ALL,
                    label: "All meetings",
                  },
                  {
                    value: MeetingType.ONE_ON_ONE,
                    label: "1-on-1s",
                  },
                  {
                    value: MeetingType.GROUP,
                    label: "Group meetings",
                  },
                ]}
                onChange={(opt) => setMeetingTypeFilter(opt.value)}
                aria-label="Meeting type filter"
                buttonProps={{ size: ButtonSize.medium }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={classNames(
          "absolute inset-0 overflow-y-scroll px-6 pt-4 pb-12",
          "top-14",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && meetingsToDisplay.length === 0 ? (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        ) : meetingsToDisplay.length === 0 ? (
          <div className="text-gray-500">No meetings.</div>
        ) : (
          <OverviewMeetingList
            ref={todayRef}
            meetings={meetingsToDisplay}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={meetingDisplayedRange}
            onClickMeetingLink={onClickMeetingLink}
            loadMore={loadMore}
            onChangeLoadMore={setLoadMore}
            isShowingWeekends={true}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingOverview;
